import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

// === FRACTION FORMATTER ===
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function simplifyFrac(p, q) {
  if (q < 0) { p = -p; q = -q; }
  const g = gcd(Math.abs(p), Math.abs(q));
  return [p / g, q / g];
}

function fracLatexSigned(p, q) {
  if (q === 1) return `${p}`;
  const neg = (p < 0) !== (q < 0);
  const A = Math.abs(p), B = Math.abs(q);
  return `${neg ? "-" : ""}\\tfrac{${A}}{${B}}`;
}

function bestLatexFromNumDen(p, q) {
  [p, q] = simplifyFrac(p, q);
  if (q === 1) return `${p}`;
  const absP = Math.abs(p), absQ = Math.abs(q);
  const neg = p < 0;
  if (absP > absQ) {
    const whole = Math.floor(absP / absQ);
    const rem = absP % absQ;
    if (rem === 0) return `${neg ? "-" : ""}${whole}`;
    return `${neg ? "-" : ""}${whole}\\,\\tfrac{${rem}}{${absQ}}`;
  }
  return `${neg ? "-" : ""}\\tfrac{${absP}}{${absQ}}`;
}

function formatBracketMultiplier(value) {
  if (value === -1) return "-";
  return `${value}`;
}

function formatLinearBracket(ax, b) {
  const xPart = ax === 1 ? "x" : `${ax}x`;
  if (b === 0) return `(${xPart})`;
  return `(${xPart} ${b > 0 ? `+ ${b}` : `- ${Math.abs(b)}`})`;
}

function formatSide(multiplier, ax, b, constant) {
  const parts = [`${formatBracketMultiplier(multiplier)}${formatLinearBracket(ax, b)}`];
  if (constant !== 0) {
    parts.push(constant > 0 ? `+ ${constant}` : `- ${Math.abs(constant)}`);
  }
  return parts.join(" ");
}


function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  const maxDen = 10;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      if (intPart === 0) return `${sign}\\tfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

// === GENERATE TASK ===
export function generateTask() {
  let ax1, ax2, b1, b2, a, d, c1, c2, x;

  do {
    ax1 = Math.floor(Math.random() * 4 + 2); // krotność x w 1. nawiasie (2–5)
    ax2 = Math.floor(Math.random() * 4 + 2); // w 2. nawiasie (2–5)

    b1 = Math.floor(Math.random() * 5 - 2); // [-2, 2]
    b2 = Math.floor(Math.random() * 5 - 2);
    if (b1 === 0 || b2 === 0) continue;

    a = Math.floor(Math.random() * 4 + 1) * (Math.random() < 0.5 ? 1 : -1);
    d = Math.floor(Math.random() * 4 + 1) * (Math.random() < 0.5 ? 1 : -1);
    if (a === 1 || d === 1) continue;
    if (a === 0 || d === 0 || a * ax1 === d * ax2) continue;

    c1 = Math.floor(Math.random() * 11 - 5);
    c2 = Math.floor(Math.random() * 11 - 5);
    if (c1 === 0 && c2 === 0) continue;

    const left = a * b1 + c1;
    const right = d * b2 + c2;

    const numerator = right - left;
    const denominator = a * ax1 - d * ax2;

    x = numerator / denominator;
  } while (
    Math.abs(x) > 10 ||
    !isFinite(x) ||
    isNaN(x)
  );

  const latex = `${formatSide(a, ax1, b1, c1)} = ${formatSide(d, ax2, b2, c2)}`;

  return {
    type: "SolveLinearEquationWithParenthesesTask",
    data: { a, ax1, b1, c1, d, ax2, b2, c2, x, latex }
  };
}


// === RENDER TASK ===
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż równanie: <BlockMath math={data.latex} />
    </p>
  );
}

// === INPUT ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math="x = " /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="x"
      />
    </div>
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  const parsed = userAnswer?.parsed;
  if (parsed == null || isNaN(parsed)) return false;
  return Math.abs(parsed - data.x) < 0.01;
}

// === RENDER ANSWER ===
// === RENDER ANSWER ===
export function renderAnswer({ data }) {
  const { a, ax1, b1, c1, d, ax2, b2, c2 } = data;

  // num = (prawa stała) - (lewa stała), den = (lewy współczynnik x) - (prawy współczynnik x)
  const num = (d * b2 + c2) - (a * b1 + c1);
  const den = (a * ax1) - (d * ax2);

  // najlepsza postać: całkowita / mieszany / zwykły; minus przed ułamkiem
  const latex = `x = ${bestLatexFromNumDen(num, den)}`;
  return <InlineMath math={latex} />;
}

// === RENDER EXPLANATION ===
export function renderExplanation({ data }) {
  const { a, ax1, b1, c1, d, ax2, b2, c2, latex } = data;

  // pomocnicze
  const plusMinus = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
  const formatLinearSide = (xCoeff, constant) => {
    const parts = [];
    if (xCoeff !== 0) {
      if (xCoeff === 1) parts.push("x");
      else if (xCoeff === -1) parts.push("-x");
      else parts.push(`${xCoeff}x`);
    }
    if (constant !== 0) {
      if (!parts.length) parts.push(`${constant}`);
      else parts.push(plusMinus(constant));
    }
    return parts.length ? parts.join(" ") : "0";
  };

  // po rozwinięciu nawiasów:
  const A = a * ax1;         // współczynnik przy x po lewej
  const B = a * b1;          // stała z lewego nawiasu
  const Lraw = `${A}x ${plusMinus(B)} ${plusMinus(c1)}`;

  const D = d * ax2;         // współczynnik przy x po prawej
  const E = d * b2;          // stała z prawego nawiasu
  const Rraw = `${D}x ${plusMinus(E)} ${plusMinus(c2)}`;

  // po redukcji stałych na stronach:
  const Lconst = B + c1;
  const Rconst = E + c2;
  const Lred = formatLinearSide(A, Lconst);
  const Rred = formatLinearSide(D, Rconst);

  // przenoszenie x-ów na lewo:
  const opX = D >= 0 ? `- ${D}x` : `+ ${Math.abs(D)}x`;

  // przenoszenie liczb na prawo:
  const opC = Lconst >= 0 ? `- ${Lconst}` : `+ ${Math.abs(Lconst)}`;

  // po przeniesieniach:
  const denom = A - D;                 // współczynnik przy x
  const num   = Rconst - Lconst;       // liczba po prawej
  const denPar = denom < 0 ? `(${denom})` : `${denom}`;

  // ułamki: najpierw "surowy", potem najlepsza postać (tylko jeśli inna)
  const rawFrac  = fracLatexSigned(num, denom);
  const bestFrac = bestLatexFromNumDen(num, denom);
  const showBest = rawFrac !== bestFrac;

  // wersja "startowa" (bez 1 przed nawiasem) — użyj gotowego latex z generatora
  const start = latex;

  return (
    <div className="space-y-3" style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1:</strong> Rozwiń nawiasy i zredukuj wewnątrz stron.</p>
      {/* oryginał */}
      <BlockMath math={start} />
      {/* po rozwinięciu (oddzielnie wypisane składniki) */}
      <BlockMath math={`${Lraw} = ${Rraw}`} />
      {/* po redukcji stałych na stronach */}
      <BlockMath math={`${Lred} = ${Rred}`} />

      <p><strong>Krok 2:</strong> Przenieś wyrazy z <InlineMath math="x" /> na lewą stronę.</p>
      {/* operacja po pionowej kresce w tej samej linii */}
      <BlockMath math={`${Lred} = ${Rred}\\;\\Big|\\ ${opX}`} />
      {/* rachunek i wynik po jednej równości na linię */}
      <BlockMath math={`${Lred} ${opX} = ${Rconst}`} />
      <BlockMath math={`${formatLinearSide(A - D, Lconst)} = ${Rconst}`} />

      <p><strong>Krok 3:</strong> Przenieś liczby na prawą stronę.</p>
      <BlockMath math={`${formatLinearSide(A - D, Lconst)} = ${Rconst}\\;\\Big|\\ ${opC}`} />
      <BlockMath math={`${A - D}x = ${Rconst} ${Lconst >= 0 ? "- " + Lconst : "+ " + Math.abs(Lconst)}`} />
      <BlockMath math={`${A - D}x = ${num}`} />

      <p><strong>Krok 4:</strong> Podziel obie strony przez <InlineMath math={denPar} />.</p>
      <BlockMath math={`${A - D}x = ${num}\\;\\Big|\\ :\\ ${denPar}`} />
      {/* najpierw surowy ułamek */}
      <BlockMath math={`x = ${rawFrac}`} />
      {/* a poniżej postać po skróceniu / mieszana — tylko jeśli inna */}
      {showBest && <BlockMath math={`x = ${bestFrac}`} />}
    </div>
  );
}



// === RENDER USER ANSWER ===
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatLatexPreview(userAnswer.raw, "x");

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
